import { useCallback, useEffect, useMemo, useState } from 'react';

import SaveArtworkIcon from '@/assets/all-icons/save-artwork-icon';
import config from '@/config';
import useGoBack from '@/hooks/useGoBack';
import useTranslations from '@/i18n/useTranslations';
import fetchWrapper from '@/services/fetchWrapper.service';
import UserService from '@/services/user.service';
import XIcon from '@/shared-components/icons/components/public/XIcon';
import GenericPopup from '@/shared-components/popups/components/GenericPopup';
import SuartePlusPopup from '@/shared-components/popups/components/SuartePlusPopup';
import StaticSeoHead from '@/shared-components/seo/StaticSeoHead';
import { getSsrArtGamesBootstrap } from '@/utils/ssrArtGamesBootstrap';

import ArtGameDeleteConfirmation from '../components/ArtGameDeleteConfirmation';
import ArtGameHeader from '../components/ArtGameHeader';

import CanvasPanel from './components/CanvasPanel';
import Sidebar from './components/Sidebar';
import { useIppitsugakiGame } from './hooks/useIppitsugakiGame';

import './index.css';

const shouldShowSuartePlusPopupFromResponse = (payload) =>
  Boolean(payload?.showSuartePlusPopup ?? payload?.data?.showSuartePlusPopup);

function readInitialIppitsugakiSeoPayload() {
  const payload = getSsrArtGamesBootstrap('ippitsugaki');
  return payload?.seoData && typeof payload.seoData === 'object' ? payload.seoData : null;
}

export default function Ippitsugaki() {
  const t = useTranslations('common.newspaper.art_games.ippitsugaki');
  const galleryT = useTranslations('common.newspaper.art_games.ippitsugaki.sidebar');
  const deleteT = useTranslations('common.newspaper.art_games.delete_dialog');
  const goBackHandler = useGoBack('/1/newspaper/art-games');
  const {
    canvasRef,
    canvasContainerRef,
    cursorPosition,
    deletePiece,
    mode,
    setMode,
    inspirationTopic,
    shuffleInspirationTopic,
    stabilizerEnabled,
    toggleStabilizer,
    savedPieces,
    saveToast,
    successStreak,
    hasStroke,
    isDeleting,
    isCurrentPieceSaved,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handlePointerCancel,
    handleReset,
    handleSave,
  } = useIppitsugakiGame({ onUpgradeRequired: () => setShowSuartePlusPopup(true) });
  const [previewPiece, setPreviewPiece] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [showSuartePlusPopup, setShowSuartePlusPopup] = useState(false);
  const [isDownloadRegistering, setIsDownloadRegistering] = useState(false);
  const [ippitsugakiSeo, setIppitsugakiSeo] = useState(() => readInitialIppitsugakiSeoPayload());
  const isGalleryEmpty = savedPieces.length === 0;

  useEffect(() => {
    let ignore = false;

    async function loadIppitsugakiSeo() {
      try {
        const { response, data } = await fetchWrapper.get(`${config.apis.api.url}/seo/art-games/ippitsugaki`);
        if (ignore || !response?.ok || !data || typeof data !== 'object') {
          return;
        }

        setIppitsugakiSeo(data);
      } catch (error) {
        if (!ignore) {
          console.error('Failed to load Ippitsugaki SEO payload', error);
        }
      }
    }

    loadIppitsugakiSeo();

    return () => {
      ignore = true;
    };
  }, []);

  const handlePieceSelect = useCallback((piece) => {
    setIsGalleryOpen(false);
    setPreviewPiece(piece);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewPiece(null);
    setShowDeleteConfirmation(false);
  }, []);

  const handleDeletePreview = useCallback(async () => {
    if (!previewPiece) return;
    const wasDeleted = await deletePiece(previewPiece.id);
    if (wasDeleted) {
      setShowDeleteConfirmation(false);
      setPreviewPiece(null);
    }
  }, [deletePiece, previewPiece]);

  const handleOpenGallery = useCallback(() => {
    if (isGalleryEmpty) return;
    setIsGalleryOpen(true);
  }, [isGalleryEmpty]);

  const handleCloseGallery = useCallback(() => {
    setIsGalleryOpen(false);
  }, []);

  const handleDownloadPreview = useCallback(async () => {
    if (!previewPiece?.url || isDownloadRegistering) return;

    const filenameDate = previewPiece.timestamp
      ? new Date(previewPiece.timestamp).toISOString().split('T')[0]
      : 'ippitsugaki';

    const triggerDownload = (dataUrl) => {
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `suarte-ippitsugaki-${filenameDate}.png`;
      link.rel = 'noopener';
      link.click();
    };

    setIsDownloadRegistering(true);
    try {
      const { response, data } = await UserService.registerIppitsugakiDownload();
      if (shouldShowSuartePlusPopupFromResponse(data)) {
        setShowSuartePlusPopup(true);
        return;
      }

      if (!response?.ok) {
        console.error('Failed to register Ippitsugaki download', data);
        return;
      }

      triggerDownload(previewPiece.url);
    } catch (error) {
      console.error('Failed to register Ippitsugaki download', error);
    } finally {
      setIsDownloadRegistering(false);
    }
  }, [isDownloadRegistering, previewPiece]);

  const previewTags = useMemo(() => {
    if (!previewPiece) return [];
    const tags = [];
    if (previewPiece.mode === 'inspiration' && previewPiece.inspirationTopic) {
      tags.push(t('preview.prompt_tag', { topic: previewPiece.inspirationTopic }));
    }
    if (previewPiece.wasEyesClosed) {
      tags.push(t('preview.eyes_closed_tag'));
    }
    return tags;
  }, [previewPiece, t]);

  return (
    <>
      <StaticSeoHead
        title={ippitsugakiSeo?.title || t('meta.title')}
        description={ippitsugakiSeo?.metaDescription || t('meta.description')}
        canonicalUrl={ippitsugakiSeo?.canonicalUrl}
        canonicalPath={ippitsugakiSeo?.canonicalPath || '/newspaper/art-games/ippitsugaki'}
        robots={ippitsugakiSeo?.robots}
        ogType={ippitsugakiSeo?.ogType}
        image={ippitsugakiSeo?.ogImage}
        pageType={ippitsugakiSeo?.pageType || 'WebApplication'}
        structuredData={ippitsugakiSeo?.structuredData}
      />
      <div className="ippitsugaki">
        <ArtGameHeader
          className="ippitsugaki__header"
          title={t('hero.title')}
          onBack={goBackHandler}
          rightSlot={
            <button
              type="button"
              className={`ippitsugaki__gallery-button${isGalleryEmpty ? '' : ' is-active'}`}
              onClick={handleOpenGallery}
              disabled={isGalleryEmpty}
              aria-label={galleryT('saved_gallery.title')}
            >
              <span className="ippitsugaki__gallery-button-label">{galleryT('saved_gallery.button')}</span>
              <SaveArtworkIcon className={`ippitsugaki__gallery-button-icon${isGalleryEmpty ? '' : ' fill'}`} />
            </button>
          }
          onShare={() => {
            const shareUrl =
              typeof window !== 'undefined' && window.location?.origin
                ? `${window.location.origin}/newspaper/art-games/ippitsugaki`
                : 'https://suarte.art/newspaper/art-games/ippitsugaki';

            if (navigator?.share) {
              navigator.share({
                title: t('share.title'),
                text: t('share.text'),
                url: shareUrl,
              }).catch(() => {});
              return;
            }

            if (navigator?.clipboard?.writeText) {
              navigator.clipboard.writeText(shareUrl).catch(() => {});
            }
          }}
          shareAriaLabel={t('share.aria')}
        />

        <section className="ippitsugaki__workspace" aria-label={t('workspace_aria')}>
          <CanvasPanel
            canvasRef={canvasRef}
            canvasContainerRef={canvasContainerRef}
            mode={mode}
            setMode={setMode}
            cursorPosition={cursorPosition}
            inspirationTopic={inspirationTopic}
            onShuffleTopic={shuffleInspirationTopic}
            saveToast={saveToast}
            stabilizerEnabled={stabilizerEnabled}
            toggleStabilizer={toggleStabilizer}
            hasStroke={hasStroke}
            isCurrentPieceSaved={isCurrentPieceSaved}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerCancel}
            onReset={handleReset}
            onSave={handleSave}
          />
        </section>
      </div>
      <GenericPopup
        open={isGalleryOpen}
        className="ippitsugaki-gallery-popup"
        backgroundClassName="ippitsugaki-gallery-popup__background"
        opacity
      >
        <Sidebar
          savedPieces={savedPieces}
          successStreak={successStreak}
          onPieceSelect={handlePieceSelect}
          isPopup
          onClose={handleCloseGallery}
        />
      </GenericPopup>
      <GenericPopup
        open={Boolean(previewPiece)}
        onCloseAction={() => setShowDeleteConfirmation(true)}
        closeButtonText={deleteT('confirm')}
        onConfirm={handleDownloadPreview}
        confirmButtonText={t('preview.download')}
        className="ippitsugaki-preview-popup"
        opacity
      >
        {previewPiece && (
          <div className="ippitsugaki-preview">
            <XIcon onClick={handleClosePreview} />
            <div className="ippitsugaki-preview__image-wrap">
              <img
                src={previewPiece.url}
                alt={t('preview.image_alt')}
                className="ippitsugaki-preview__image"
              />
            </div>
            <div className="ippitsugaki-preview__meta">
              <p className="ippitsugaki-preview__timestamp">
                {new Date(previewPiece.timestamp).toLocaleString()}
              </p>
              {previewTags.length > 0 && (
                <ul className="ippitsugaki-preview__tags">
                  {previewTags.map((tag) => (
                    <li key={tag}>{tag}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </GenericPopup>
      <ArtGameDeleteConfirmation
        open={showDeleteConfirmation}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={handleDeletePreview}
        isDeleting={isDeleting}
      />
      <SuartePlusPopup
        open={showSuartePlusPopup}
        onClose={() => setShowSuartePlusPopup(false)}
        highlightedBenefit="game_saves"
      />
    </>
  );
}
